/**
 * Sanitises a stored "send her back here afterwards" path before it reaches
 * `navigate()`.
 *
 * ⚠️ WHY THIS EXISTS, precisely.
 *
 * `useKroger.tsx` stashes where she was before the OAuth round-trip:
 *
 *     sessionStorage.setItem(KEY, window.location.pathname + window.location.search);
 *
 * That is her own location, not a URL parameter, so it is not a
 * straightforward open redirect. But `window.location.pathname` **can begin
 * with `//`** — verified:
 *
 *     new URL("https://app.kinyoudoitall.com//evil.example").pathname
 *       === "//evil.example"
 *
 * and `navigate("//evil.example")` is reinterpreted as a **protocol-relative
 * URL**, sending her off-origin. That is GHSA-2j2x-hqr9-3h42 against
 * react-router; GHSA-wrjc-x8rr-h8h6 is the same shape using a backslash.
 *
 * The full chain — land on a `//`-prefixed URL, start the Kroger connect
 * flow, get redirected off-site after the round-trip — is convoluted but not
 * impossible, and this guard costs nothing. It is deliberately independent of
 * the react-router version: upgrading fixes the library, this fixes the input,
 * and the input is the part we control.
 *
 * Related: #7 (npm audit triage).
 */

/** Where to send her when the stored path can't be trusted. */
export const RETURN_PATH_FALLBACK = "/";

/**
 * Returns a same-origin, root-relative path, or {@link RETURN_PATH_FALLBACK}.
 *
 * @param raw    the stored value — untrusted, may be null
 * @param origin the current origin, e.g. `window.location.origin`
 */
export function safeReturnPath(raw: string | null | undefined, origin: string): string {
  if (!raw) return RETURN_PATH_FALLBACK;

  // Must be root-relative. Rejects "https://evil.example", "javascript:…",
  // "evil.example/path", and anything else carrying its own scheme or host.
  if (!raw.startsWith("/")) return RETURN_PATH_FALLBACK;

  // ⚠️ The actual bug. "//host" is protocol-relative; "/\host" is the
  // backslash variant browsers normalise to the same thing. Both begin with a
  // single "/" and would pass a naive startsWith("/") check.
  if (/^\/[/\\]/.test(raw)) return RETURN_PATH_FALLBACK;

  // Belt-and-braces: let the URL parser have the final say, because it applies
  // the same normalisation the browser will. Anything that resolves off-origin
  // — however it got there — does not come back.
  try {
    const url = new URL(raw, origin);
    if (url.origin !== origin) return RETURN_PATH_FALLBACK;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return RETURN_PATH_FALLBACK;
  }
}
